// =============================================================================
// src/lib/worklist/assign.js - assigning worklist items to a person.
//
// Mirrors worklist_owner_role_of / worklist_role_can_act (migration
// 20261001210000_worklist_assign) so the picker only offers people who can
// work the item. The server re-checks everything in worklist_assign.
// =============================================================================
import { useEffect, useMemo } from "react";
import { supabase } from "../supabaseClient";
import usePracticeUsers from "../usePracticeUsers";

export const ASSIGNER_ROLES = ["Owner", "Manager", "Supervising Care Manager", "Care Manager Supervisor"];

const OWNER = {
  unsigned_note: "Provider", result_ack: "Provider", message: "Provider",
  insurance_review: "FrontDesk", eligibility_failed: "FrontDesk", expiring_consent: "FrontDesk", confirm_call: "FrontDesk",
  denial: "Billing", billing_hold: "Billing", billing_aging: "Billing",
  rooming: "MA", refill_request: "MA", chw_resource_followup: "CHW",
  referral: "AnyStaff", task: "AnyStaff",
};

export function roleCanAct(role, itemType) {
  if (!role) return false;
  if (role === "Owner" || role === "Manager") return true;
  switch (OWNER[itemType] || "CM") {
    case "AnyStaff": return role !== "Patient" && role !== "System";
    case "CM": return ["Care Manager", "Supervising Care Manager", "Care Manager Supervisor"].includes(role);
    case "Provider": return ["Provider", "RN", "LPN", "Medical Assistant"].includes(role);
    case "FrontDesk": return role === "Front Desk" || (itemType === "eligibility_failed" && role === "Billing");
    case "Billing": return role === "Billing";
    case "MA": return ["Medical Assistant", "RN", "LPN", "Provider"].includes(role);
    case "CHW": return role === "CHW";
    default: return false;
  }
}

// Active staff (id, full_name, role) for the practice. Reads the shared staff
// list (usePracticeUsers, round 6) instead of its own users query, so the
// picker and the worklist's claim names cost one request between them.
export function useAssignableStaff(practiceId, enabled = true) {
  const { users, error } = usePracticeUsers(practiceId, enabled);
  useEffect(() => {
    if (error) console.warn("[worklist] staff list failed:", error.message);
  }, [error]);
  // Active only: anyone not switched off (is_active false). The old query
  // asked the server for is_active = true; a row with no flag is not a
  // deactivated person, so it stays pickable.
  return useMemo(() => (enabled ? users : [])
    .filter((u) => u.is_active !== false && u.role !== "Patient" && u.role !== "System"), [users, enabled]);
}

export async function assignItem(row, userId) {
  const { data, error } = await supabase.rpc("worklist_assign", {
    p_item_type: row.item_type, p_source_id: row.source_id, p_user_id: userId,
  });
  if (error) throw error;
  return data;
}
